/**
 * Account API client — used by Settings → Account tab.
 *
 * The shape mirrors `/api/account` exactly (see src/routes/account.ts).
 */

import { apiFetch } from './api';

export type SignInProvider = 'google' | 'credential' | 'unknown';

export interface AccountProfile {
  id: string;
  email: string;
  name: string | null;
  image: string | null;
  emailVerified: boolean;
  /** Unix milliseconds — Better Auth convention. */
  createdAt: number;
  signInProvider: SignInProvider;
}

export interface AccountBillingInfo {
  tier: 'free' | 'pro' | 'scale';
  hasActiveSubscription: boolean;
  subscriptionStatus: string | null;
}

export interface AccountResourceCounts {
  drafts: number;
  sends: number;
  domains: number;
  apiKeys: number;
  integrations: number;
}

export interface AccountResponse {
  profile: AccountProfile;
  billing: AccountBillingInfo;
  counts: AccountResourceCounts;
}

export async function getAccount(): Promise<AccountResponse> {
  return apiFetch<AccountResponse>('/api/account');
}

export interface UploadAvatarResponse {
  url: string;
  key: string;
}

/**
 * Upload a new avatar image. Server validates MIME (png/jpeg/gif/webp,
 * no SVG) and enforces a 2 MiB cap. The returned URL is already public
 * (CDN custom domain in prod, Worker proxy in local dev) and is what we
 * write into `user.image`.
 */
export async function uploadAvatar(file: File): Promise<UploadAvatarResponse> {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch<UploadAvatarResponse>('/api/account/avatar', {
    method: 'POST',
    body: fd,
  });
}

/** Clear the user's avatar (UI falls back to initials). */
export async function removeAvatar(): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/account/avatar', { method: 'DELETE' });
}

/**
 * Permanently delete the signed-in account. Requires the caller to echo
 * their own email as a typed confirmation token — matches GitHub's
 * destructive-action UX.
 *
 * On success the server clears session cookies and returns `{ ok: true }`.
 * Callers should navigate to `/sign-in` (or the landing page) afterwards.
 */
export async function deleteAccount(confirmEmail: string): Promise<{ ok: true }> {
  return apiFetch<{ ok: true }>('/api/account', {
    method: 'DELETE',
    json: { confirm: confirmEmail },
  });
}
